import styled from 'styled-components'
import { fontSize, fontWeight, color, unit } from 'ui/themes/utils'

const NavigationButton = styled.span`
  position: absolute;
  top: ${unit('0.5')};

  color: ${color('Text/Primary')};

  font-size: ${fontSize()} !important;

  cursor: pointer;

  transition: opacity 0.2s ease-in-out;
  will-change: opacity;

  &:hover {
    opacity: 0.3;
  }
`

export const StyledDayPicker = styled.div`
  --cell-size: 50px;
`

export const NavigationPrevious = styled(NavigationButton)`
  left: 0;
`

export const NavigationNext = styled(NavigationButton)`
  right: 0;
`

export const DayPicker = styled.div`
  --cell-size: 50px;

  font-family: ${(props) => props.theme.font.body};

  * {
    outline: none;
  }

  .DayPicker {
    display: block;
  }

  /* Overwrite of some initial styles */
  .DayPicker:not(.DayPicker--interactionDisabled)
    .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):hover {
    background-color: initial;
  }

  .DayPicker-wrapper {
    position: relative;

    user-select: none;

    flex-direction: row;

    padding: 0;
  }

  .DayPicker-Month {
    display: block;

    padding: 0;
    margin: 0;

    border-collapse: collapse;
    border-spacing: 0;
    user-select: none;
  }

  .DayPicker-Caption {
    display: flex;
    justify-content: center;
    align-items: center;

    color: ${color('Text/Primary')};

    text-align: center;

    padding: 0 ${unit('5')};
    margin-bottom: ${unit('2')};

    & > div {
      font-size: 16px;
      font-weight: ${fontWeight('semibold')};
    }
  }

  .DayPicker-Weekdays {
    display: flex;

    margin-top: ${unit('2')};
  }

  .DayPicker-WeekdaysRow {
    display: flex;

    padding-top: ${unit('1')};
    margin-bottom: ${unit('2')};
  }

  .DayPicker-Weekday {
    display: flex;
    justify-content: center;
    align-items: center;

    width: var(--cell-size);

    padding: 0;

    font-size: ${fontSize('md')};
    font-weight: ${fontWeight('semibold')};
    text-align: center;

    color: ${color('Text/Tertiary')};

    text-transform: uppercase;
  }

  .DayPicker-Weekday abbr[title] {
    border-bottom: none;
    text-decoration: none;
  }

  .DayPicker-Week {
    display: flex;

    &:not(:last-child) {
      /* Negative margin to collapse selected borders */
      margin-bottom: -2px;
    }
  }

  .DayPicker-Day {
    box-sizing: border-box;
    display: block;

    width: var(--cell-size);
    height: var(--cell-size);

    padding: 0;

    border: none;
    border-radius: 0;

    .DayPicker-Day__wrapper {
      box-sizing: border-box;

      width: var(--cell-size);
      height: var(--cell-size);

      padding: 3px;

      position: relative;
    }

    .DayPicker-Day__inner {
      box-sizing: border-box;

      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      width: 100%;
      height: 100%;

      border-radius: 50%;

      position: relative;

      z-index: 1;
    }

    .DayPicker-Day__date {
      color: ${color('Text/Primary')};

      font-weight: ${fontWeight('lighter')};
      font-size: ${fontSize('md')};
      line-height: 20px;

      text-align: center;
    }

    .DayPicker-Day__availability-count {
      position: absolute;
      bottom: 6px;
      left: 50%;

      color: ${color('Text/Primary')};

      transform: translateX(-50%);

      font-size: 10px;
      line-height: 12px;

      text-align: center;
    }

    &__availability {
      &.available {
        .DayPicker-Day__inner {
          background-color: #60d672cc;
        }
      }

      &.unavailable {
        .DayPicker-Day__inner {
          background-color: #e51c2ccc;

          .DayPicker-Day__date,
          .DayPicker-Day__availability-count {
            color: ${color('Primary/Foreground1')};
          }
        }
      }

      &.partial {
        .DayPicker-Day__inner {
          background-color: #ffb442cc;

          .DayPicker-Day__date,
          .DayPicker-Day__availability-count {
            color: ${color('Warning/Foreground1')};
          }
        }
      }
    }

    &--past {
      .DayPicker-Day__availability-count {
        display: none;
      }
    }

    &--selected {
      &:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {
        background-color: transparent;
        color: initial;
      }

      & > .DayPicker-Day__wrapper {
        border-color: transparent;

        &::after {
          content: '';

          position: absolute;
          top: 0px;
          bottom: 0px;
          left: 0px;
          right: 0px;

          z-index: 10;

          border: 2px solid ${color('Primary/Outline')};
          border-left: none;
          border-right: none;
        }
      }
    }

    &--from > .DayPicker-Day__wrapper {
      &::after {
        content: '';

        position: absolute;
        top: 0px;
        bottom: 0px;
        left: 0px;
        right: 0px;

        z-index: 10;

        border: 2px solid ${color('Primary/Outline')};
        border-right: none;
        border-top-left-radius: 50%;
        border-bottom-left-radius: 50%;
      }
    }

    &--to > .DayPicker-Day__wrapper {
      &::after {
        content: '';

        position: absolute;
        top: 0px;
        bottom: 0px;
        right: 0px;
        left: 0px;

        z-index: 10;

        border: 2px solid ${color('Primary/Outline')};
        border-left: none;
        border-top-right-radius: 50%;
        border-bottom-right-radius: 50%;

        box-sizing: border-box;
      }
    }

    &--today {
      .DayPicker-Day__date {
        font-weight: ${fontWeight('bold')};
        text-decoration: underline;
        text-underline-offset: 3px;
      }
    }

    &--disabled {
      cursor: not-allowed;

      .DayPicker-Day__availability {
        &.available {
          .DayPicker-Day__inner {
            background-image: repeating-linear-gradient(
              315deg,
              ${color('Success/Background1')},
              ${color('Success/Background1')} 6px,
              ${color('Primary/Foreground1')} 6px,
              ${color('Primary/Foreground1')} 7px
            );

            .DayPicker-Day__date,
            .DayPicker-Day__availability-count {
              color: ${color('Success/Foreground3')};
            }
          }
        }

        &.unavailable {
          .DayPicker-Day__inner {
            background-image: repeating-linear-gradient(
              315deg,
              ${color('Danger/Background1')},
              ${color('Danger/Background1')} 6px,
              ${color('Primary/Foreground1')} 6px,
              ${color('Primary/Foreground1')} 7px
            );

            .DayPicker-Day__date,
            .DayPicker-Day__availability-count {
              color: ${color('Danger/Foreground3')};
            }
          }
        }

        &.partial {
          .DayPicker-Day__inner {
            background-image: repeating-linear-gradient(
              315deg,
              ${color('Warning/Background1')},
              ${color('Warning/Background1')} 6px,
              ${color('Primary/Foreground1')} 6px,
              ${color('Primary/Foreground1')} 7px
            );

            .DayPicker-Day__date,
            .DayPicker-Day__availability-count {
              color: ${color('Warning/Foreground3')};
            }
          }
        }
      }

      .DayPicker-Day__inner {
        background-image: repeating-linear-gradient(
          315deg,
          ${color('Secondary/Background1')},
          ${color('Secondary/Background1')} 6px,
          ${color('Primary/Foreground1')} 6px,
          ${color('Primary/Foreground1')} 7px
        );

        .DayPicker-Day__date,
        .DayPicker-Day__availability-count {
          color: ${color('Text/Primary')}66;
        }
      }

      &:hover {
        .DayPicker-Day__inner {
          background-image: repeating-linear-gradient(
            315deg,
            ${color('Secondary/Background2')},
            ${color('Secondary/Background2')} 6px,
            ${color('Primary/Foreground1')} 6px,
            ${color('Primary/Foreground1')} 7px
          );
        }

        .DayPicker-Day__availability {
          &.available {
            .DayPicker-Day__inner {
              background-image: repeating-linear-gradient(
                315deg,
                ${color('Success/Background2')},
                ${color('Success/Background2')} 6px,
                ${color('Primary/Foreground1')} 6px,
                ${color('Primary/Foreground1')} 7px
              );
            }
          }

          &.unavailable {
            .DayPicker-Day__inner {
              background-image: repeating-linear-gradient(
                315deg,
                ${color('Danger/Background2')},
                ${color('Danger/Background2')} 6px,
                ${color('Primary/Foreground1')} 6px,
                ${color('Primary/Foreground1')} 7px
              );
            }
          }

          &.partial {
            .DayPicker-Day__inner {
              background-image: repeating-linear-gradient(
                315deg,
                ${color('Warning/Background2')},
                ${color('Warning/Background2')} 6px,
                ${color('Primary/Foreground1')} 6px,
                ${color('Primary/Foreground1')} 7px
              );
            }
          }
        }
      }
    }

    &--selection:not(.DayPicker-Day--outside) {
      position: relative;

      &::before {
        content: '';

        position: absolute;
        top: 0px;
        bottom: 0px;
        left: 0px;
        right: 0px;

        background-color: ${color('Primary/Background2')};
      }
    }

    &--selectionStart:not(.DayPicker-Day--outside) {
      &::before {
        border-top-left-radius: 50%;
        border-bottom-left-radius: 50%;
      }
    }

    &--selectionEnd:not(.DayPicker-Day--outside) {
      &::before {
        border-top-right-radius: 50%;
        border-bottom-right-radius: 50%;
      }
    }

    &:has(.DayPicker-Day__availability-count) .DayPicker-Day__date {
      transform: translateY(-7px);
    }

    &:hover {
      .DayPicker-Day__inner {
        background-color: ${color('Primary/Background2')};
      }

      .DayPicker-Day__availability {
        &.available {
          .DayPicker-Day__inner {
            background-color: #40a14fcc;
          }
        }

        &.unavailable {
          .DayPicker-Day__inner {
            background-color: #c1000fcc;
          }
        }

        &.partial {
          .DayPicker-Day__inner {
            background-color: #e08700cc;
          }
        }
      }
    }
  }

  .DayPicker-Day--from.DayPicker-Day--to > .DayPicker-Day__wrapper {
    border-radius: 50%;

    &::after {
      content: '';
      position: absolute;

      top: 0px;
      bottom: 0px;
      left: 0px;
      right: 0px;

      z-index: 10;

      width: 100%;

      border: 2px solid ${color('Primary/Outline')};
      border-radius: 50%;
    }
  }

  @media screen and (min-width: 866px) {
    .DayPicker-Months {
      justify-content: space-between;
    }
  }
`
